/**
 * Plugin file-intake destinations (ADR-030) — the plugin apps the signed-in
 * user may open a Vault file in, and the file kinds each opens.
 *
 * Internal arches register in code (`ARCHE_HANDOFF_INTAKE`). Plugin arche ids
 * are per-environment and their approval is database state, so plugins come
 * from `GET /v1/arches/file-intakes` instead. Every fetch refreshes the
 * synchronous snapshot (`plugin-intake-snapshot.ts`) the handoff gates read;
 * `ensurePluginIntakes` lets a gate that may run before the first fetch wait.
 *
 * The server is the authority: the list carries effective kinds only
 * (admin-approved ∩ declared), filtered to arches this user may use.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type FileIntakeDestination } from '@/lib/api';
import { queryClient } from '@/lib/query-client';
import { queryKeys } from '@/lib/query-keys';
import { handoffTargetsArche, type ArcheHandoffPayload } from './handoff';
import { setPluginIntakeSnapshot } from './plugin-intake-snapshot';

async function fetchPluginIntakes(): Promise<FileIntakeDestination[]> {
  // Errors propagate so React Query retries; the hook maps a failure to "no
  // plugin destinations" (signed out, a portal — where opening a file in a
  // plugin is refused — or the API is down) without caching it as an answer.
  const { destinations } = await api.getFileIntakeDestinations();
  setPluginIntakeSnapshot(destinations);
  return destinations;
}

const QUERY = {
  queryKey: queryKeys.fileIntake.list(),
  queryFn: fetchPluginIntakes,
  staleTime: 5 * 60 * 1000,
} as const;

const EMPTY: FileIntakeDestination[] = [];

/**
 * Fetches the list fresh from the server, whatever is cached. The host calls
 * this immediately before granting a file to a plugin, so a withdrawn
 * approval takes effect at the next handoff — never "once the cache ages
 * out", which an open tab may never do. Throws if the list is unreachable:
 * no fresh answer, no grant.
 */
export async function refreshPluginIntakes(): Promise<void> {
  setPluginIntakeSnapshot(await queryClient.fetchQuery({ ...QUERY, staleTime: 0 }));
}

/** Fetches the list if it has not been fetched yet; resolves once the snapshot is current. */
export async function ensurePluginIntakes(): Promise<void> {
  try {
    setPluginIntakeSnapshot(await queryClient.ensureQueryData(QUERY));
  } catch {
    // Unreachable list: the gates answer "not a destination".
  }
}

/**
 * The plugin destinations, for UI that lists them ("Open in…"). Pass
 * `enabled: false` while signed out: nothing is fetched and the snapshot is
 * emptied, so one user's list never outlives their session.
 */
export function useFileIntakeArches(options: { enabled?: boolean } = {}): FileIntakeDestination[] {
  const enabled = options.enabled ?? true;
  const { data } = useQuery({ ...QUERY, enabled });
  useEffect(() => {
    if (!enabled) setPluginIntakeSnapshot(EMPTY);
  }, [enabled]);
  return (enabled ? data : undefined) ?? EMPTY;
}

/**
 * `handoffTargetsArche` for callers that may run before the list has loaded:
 * for a plugin `open_document` target it waits for the first fetch instead of
 * answering from an empty snapshot.
 */
export async function handoffTargetsArcheAsync(
  payload: ArcheHandoffPayload,
  archeId: string,
): Promise<boolean> {
  if (handoffTargetsArche(payload, archeId)) return true;
  if (payload.kind !== 'open_document' || !archeId.startsWith('arc_')) return false;
  await ensurePluginIntakes();
  return handoffTargetsArche(payload, archeId);
}
